import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Sidebar from './Sidebar';
import Header from './Header';
import PageHeader from './ui/PageHeader';
import { useTheme } from '../contexts/ThemeContext';
import { useSidebar } from '../contexts/SidebarContext';
import { usePageTitle } from '../hooks/usePageTitle';
import {
  Settings, Loader2,
  Sparkles, Video,
  ChevronRight, Puzzle, GraduationCap, ArrowRight
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL;

export default function ExtensionDashboard({ onLogout }) {
  usePageTitle('DarkPlanner Tools');
  const { theme } = useTheme();
  const { sidebarWidth, isMobile } = useSidebar();
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [aiConfigured, setAiConfigured] = useState(false);
  const [rewriteStatus, setRewriteStatus] = useState(null); // { has_global_api, has_user_api, daily_limit, used_today, remaining }

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const [settingsRes, statusRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/extension/ai/settings`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${BACKEND_URL}/api/extension/ai/rewrite-status`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => null)
      ]);
      if (settingsRes.data.success && settingsRes.data.settings) {
        setAiConfigured(!!settingsRes.data.settings.ai_api_key);
      }
      if (statusRes?.data?.success) {
        setRewriteStatus(statusRes.data);
      }
    } catch { /* silent */ }
    finally { setLoading(false); }
  };

  const openVEO = () => window.open('https://labs.google/fx/pt/tools/video-fx', '_blank');
  const openWhisk = () => window.open('https://labs.google/fx/pt/tools/whisk', '_blank');
  
  if (loading) {
    return (
      <div style={{ display: 'flex', minHeight: '100vh', background: 'var(--dp-bg-primary)' }}>
        <Sidebar onLogout={onLogout} />
        <Header />
        <main className="dp-main-content" style={{
          marginLeft: isMobile ? 0 : `${sidebarWidth}px`,
          marginTop: '60px',
          flex: 1,
          minWidth: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'margin-left 0.3s ease',
          fontFamily: 'var(--dp-font)',
        }}>
          <Loader2 size={24} style={{ color: 'var(--dp-blue-500)', animation: 'spin 1s linear infinite' }} />
        </main>
      </div>
    );
  }
  
  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: 'var(--dp-bg-primary)' }}>
      <Sidebar onLogout={onLogout} />
      <Header />

      <main className="dp-main-content" style={{
        marginLeft: isMobile ? 0 : `${sidebarWidth}px`,
        marginTop: '60px',
        flex: 1,
        minWidth: 0,
        transition: 'margin-left 0.3s ease',
        fontFamily: 'var(--dp-font)',
        animation: 'dp-pageFadeIn 0.35s ease',
      }}>
          {/* PageHeader padrão */}
          <PageHeader
            icon={<Video size={20} style={{ color: 'var(--dp-blue-500)' }} />}
            title={t('extension.title')}
            description={t('extension.subtitle')}
          />
          
          {/* Módulos */}
          <div style={{ marginBottom: '24px' }}>
            <p style={{
              fontSize: '11px',
              fontWeight: '600',
              color: 'var(--dp-text-secondary)',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              margin: '0 0 12px 0'
            }}>
              {t('extension.select_module')}
            </p>
            
            {/* VEO Card */}
            <div
              className="dp-top-accent"
              onClick={openVEO}
              style={{
                background: 'var(--dp-bg-card)',
                border: '1px solid var(--dp-border-subtle)',
                borderRadius: '8px',
                padding: '16px',
                marginBottom: '12px',
                cursor: 'pointer',
                transition: 'all 0.2s',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '12px'
              }}
              onMouseEnter={(e) => e.currentTarget.style.borderColor = 'var(--dp-blue-500, #0DAEFF)'}
              onMouseLeave={(e) => e.currentTarget.style.borderColor = 'var(--dp-border-subtle)'}
            >
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '8px',
                background: 'rgba(13, 174, 255, 0.12)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}>
                <Video size={20} style={{ color: 'var(--dp-blue-500, #0DAEFF)' }} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '2px' }}>
                  <span style={{ fontSize: '14px', fontWeight: '600', color: 'var(--dp-text-primary)' }}>VEO</span>
                  <span style={{
                    fontSize: '10px',
                    padding: '2px 6px',
                    borderRadius: '4px',
                    background: 'rgba(16, 185, 129, 0.15)',
                    color: '#10b981',
                    fontWeight: '500'
                  }}>{t('extension.active')}</span>
                </div>
                <p style={{ fontSize: '12px', color: 'var(--dp-text-secondary)', margin: '0 0 8px 0' }}>
                  {t('extension.veo_desc')}
                </p>
                <p style={{ fontSize: '11px', color: 'var(--dp-text-secondary)', margin: '0 0 10px 0', lineHeight: '1.4' }}>
                  {t('extension.veo_detail')}
                </p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {[t('extension.veo_tag_text'), t('extension.veo_tag_image'), t('extension.veo_tag_ingredients'), t('extension.veo_tag_batch')].map((tag) => (
                    <span key={tag} style={{
                      fontSize: '10px',
                      padding: '3px 8px',
                      borderRadius: '4px',
                      border: '1px solid var(--dp-border-subtle)',
                      color: 'var(--dp-text-secondary)'
                    }}>{tag}</span>
                  ))}
                </div>
              </div>
              <ChevronRight size={18} style={{ color: 'var(--dp-text-secondary)', flexShrink: 0 }} />
            </div>
            
            {/* WHISK Card */}
            <div
              className="dp-top-accent"
              onClick={openWhisk}
              style={{
                background: 'var(--dp-bg-card)',
                border: '1px solid var(--dp-border-subtle)',
                borderRadius: '8px',
                padding: '16px',
                cursor: 'pointer',
                transition: 'all 0.2s',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '12px'
              }}
              onMouseEnter={(e) => e.currentTarget.style.borderColor = 'var(--dp-blue-500, #0DAEFF)'}
              onMouseLeave={(e) => e.currentTarget.style.borderColor = 'var(--dp-border-subtle)'}
            >
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '8px',
                background: 'rgba(13, 174, 255, 0.12)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}>
                <Sparkles size={20} style={{ color: 'var(--dp-blue-500, #0DAEFF)' }} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '2px' }}>
                  <span style={{ fontSize: '14px', fontWeight: '600', color: 'var(--dp-text-primary)' }}>WHISK</span>
                  <span style={{
                    fontSize: '10px',
                    padding: '2px 6px',
                    borderRadius: '4px',
                    background: 'rgba(16, 185, 129, 0.15)',
                    color: '#10b981',
                    fontWeight: '500'
                  }}>{t('extension.active')}</span>
                </div>
                <p style={{ fontSize: '12px', color: 'var(--dp-text-secondary)', margin: '0 0 8px 0' }}>
                  {t('extension.whisk_desc')}
                </p>
                <p style={{ fontSize: '11px', color: 'var(--dp-text-secondary)', margin: '0 0 10px 0', lineHeight: '1.4' }}>
                  {t('extension.whisk_detail')}
                </p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {[t('extension.whisk_tag_text'), t('extension.whisk_tag_refs'), t('extension.whisk_tag_aspect'), t('extension.whisk_tag_download')].map((tag) => (
                    <span key={tag} style={{
                      fontSize: '10px',
                      padding: '3px 8px',
                      borderRadius: '4px',
                      border: '1px solid var(--dp-border-subtle)',
                      color: 'var(--dp-text-secondary)'
                    }}>{tag}</span>
                  ))}
                </div>
              </div>
              <ChevronRight size={18} style={{ color: 'var(--dp-text-secondary)', flexShrink: 0 }} />
            </div>

            {/* Extensão DarkPlanner Card */}
            <div
              className="dp-top-accent"
              onClick={() => window.open('https://chromewebstore.google.com/detail/darkplanner-tools/dkpcmhcjlidhmmnileiojcflhpclhcdn?authuser=0&hl=pt-BR', '_blank')}
              style={{
                background: 'var(--dp-bg-card)',
                border: '1px solid var(--dp-border-subtle)',
                borderRadius: '8px',
                padding: '16px',
                marginTop: '12px',
                cursor: 'pointer',
                transition: 'all 0.2s',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '12px'
              }}
              onMouseEnter={(e) => e.currentTarget.style.borderColor = 'var(--dp-blue-500, #0DAEFF)'}
              onMouseLeave={(e) => e.currentTarget.style.borderColor = 'var(--dp-border-subtle)'}
            >
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '8px',
                background: 'rgba(16, 185, 129, 0.15)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}>
                <Puzzle size={20} style={{ color: 'var(--dp-blue-500, #0DAEFF)' }} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '2px' }}>
                  <span style={{ fontSize: '14px', fontWeight: '600', color: 'var(--dp-text-primary)' }}>{t('extension.extension_name')}</span>
                  <span style={{
                    fontSize: '10px',
                    padding: '2px 6px',
                    borderRadius: '4px',
                    background: 'rgba(16, 185, 129, 0.15)',
                    color: '#10b981',
                    fontWeight: '500'
                  }}>{t('extension.install')}</span>
                </div>
                <p style={{ fontSize: '12px', color: 'var(--dp-text-secondary)', margin: '0 0 8px 0' }}>
                  {t('extension.extension_desc')}
                </p>
                <p style={{ fontSize: '11px', color: 'var(--dp-text-secondary)', margin: '0 0 10px 0', lineHeight: '1.4' }}>
                  {t('extension.extension_detail')}
                </p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {[t('extension.extension_tag_chrome'), t('extension.extension_tag_automation'), t('extension.extension_tag_shortcuts')].map((tag) => (
                    <span key={tag} style={{
                      fontSize: '10px',
                      padding: '3px 8px',
                      borderRadius: '4px',
                      border: '1px solid var(--dp-border-subtle)',
                      color: 'var(--dp-text-secondary)'
                    }}>{tag}</span>
                  ))}
                </div>
              </div>
              <ChevronRight size={18} style={{ color: 'var(--dp-text-secondary)', flexShrink: 0 }} />
            </div>
          </div>

          {/* Recursos e Aprendizado */}
          <div style={{ marginBottom: '24px' }}>
            <p style={{
              fontSize: '11px',
              fontWeight: '600',
              color: 'var(--dp-text-secondary)',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              margin: '0 0 12px 0'
            }}>
              {t('extension.resources_title')}
            </p>

            {/* Tutoriais card */}
            <div style={{
              background: 'var(--dp-bg-card)',
              border: '1px solid var(--dp-border-subtle)',
              borderRadius: '8px',
              padding: '16px',
              display: 'flex',
              alignItems: 'flex-start',
              gap: '12px',
            }}>
              <div style={{
                width: '36px', height: '36px', borderRadius: '8px',
                background: 'rgba(59, 130, 246, 0.12)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
              }}>
                <GraduationCap size={18} style={{ color: 'var(--dp-blue-500, #0DAEFF)' }} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--dp-text-primary)', marginBottom: '4px' }}>{t('extension.tutorials_title')}</div>
                <p style={{ fontSize: '11px', color: 'var(--dp-text-secondary)', margin: '0 0 8px 0', lineHeight: '1.4' }}>
                  {t('extension.tutorials_desc')}
                </p>
                <Link
                  to="/tutoriais"
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: '4px',
                    fontSize: '12px', fontWeight: 600, color: 'var(--dp-blue-500, #0DAEFF)',
                    textDecoration: 'none', transition: 'opacity 0.15s',
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.opacity = '0.7'}
                  onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                >
                  {t('extension.view_tutorials')} <ArrowRight size={14} />
                </Link>
              </div>
            </div>
          </div>

          {/* Configuração de IA — Badge + Link para Configurações */}
          <div style={{
            background: 'var(--dp-bg-card)',
            border: '1px solid var(--dp-border-subtle)',
            borderRadius: '8px',
            padding: '14px 16px',
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Settings size={16} style={{ color: 'var(--dp-blue-500, #0DAEFF)' }} />
                <span style={{ fontSize: '13px', fontWeight: 500, color: 'var(--dp-text-primary)' }}>
                  {t('extension.ai_settings_title')}
                </span>
                {rewriteStatus?.has_global_api ? (
                  <span style={{
                    fontSize: '10px', fontWeight: 600,
                    color: '#22C55E',
                    background: 'rgba(34, 197, 94, 0.1)',
                    border: '1px solid rgba(34, 197, 94, 0.25)',
                    padding: '2px 8px', borderRadius: '9999px',
                  }}>
                    ✅ {t('extension.global_api_active')}
                  </span>
                ) : (
                  <span style={{
                    fontSize: '10px', fontWeight: 600,
                    color: aiConfigured ? '#22C55E' : '#EF4444',
                    background: aiConfigured ? 'rgba(34, 197, 94, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                    border: `1px solid ${aiConfigured ? 'rgba(34, 197, 94, 0.25)' : 'rgba(239, 68, 68, 0.25)'}`,
                    padding: '2px 8px', borderRadius: '9999px',
                  }}>
                    {aiConfigured ? `✅ ${t('settings_apis.gemini_status_configured')}` : `❌ ${t('settings_apis.gemini_status_not_configured')}`}
                  </span>
                )}
              </div>
              {!rewriteStatus?.has_global_api && (
                <Link
                  to="/configuracoes#apis"
                  style={{
                    fontSize: '12px', fontWeight: 500, color: 'var(--dp-blue-500, #0DAEFF)',
                    textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '4px',
                  }}
                >
                  {t('settings_apis.manage')} <ChevronRight size={14} />
                </Link>
              )}
            </div>

            {/* Daily usage counter - only when global API is active */}
            {rewriteStatus?.has_global_api && (
              <div style={{
                display: 'flex', alignItems: 'center', gap: '12px',
                padding: '8px 12px', borderRadius: '6px',
                background: 'rgba(66, 133, 244, 0.06)',
                border: '1px solid rgba(66, 133, 244, 0.1)',
              }}>
                <Sparkles size={14} style={{ color: 'var(--dp-blue-500, #0DAEFF)', flexShrink: 0 }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '11px', color: 'var(--dp-text-secondary)', marginBottom: '2px' }}>
                    {t('extension.daily_rewrites')}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{
                      flex: 1, height: '4px', borderRadius: '2px',
                      background: 'rgba(255,255,255,0.06)', overflow: 'hidden',
                    }}>
                      <div style={{
                        width: `${Math.min(100, ((rewriteStatus.used_today || 0) / (rewriteStatus.daily_limit || 50)) * 100)}%`,
                        height: '100%', borderRadius: '2px',
                        background: (rewriteStatus.used_today || 0) >= (rewriteStatus.daily_limit || 50) ? '#EF4444' : 'var(--dp-blue-500, #0DAEFF)',
                        transition: 'width 0.3s ease',
                      }} />
                    </div>
                    <span style={{
                      fontSize: '11px', fontWeight: 600, color: 'var(--dp-text-primary)', whiteSpace: 'nowrap',
                    }}>
                      {rewriteStatus.used_today || 0}/{rewriteStatus.daily_limit || 50}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>

      </main>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
